import React, { useEffect, useState } from 'react';
import { Badge, Button, Container, Spinner, Table } from 'react-bootstrap';
import PageHeader from 'components/PageHeader';
import {
  getLatestUnderenhetResult,
  LatestUnderenhetUpdatesResult,
  QaRunCandidate,
  runBrregJobNow,
} from 'services/BrregOppdateringer';

const NyeUnderenheterPage: React.FC = () => {
  const [result, setResult] = useState<LatestUnderenhetUpdatesResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [runMessage, setRunMessage] = useState<string | null>(null);

  const loadResult = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getLatestUnderenhetResult();
      setResult(data);
    } catch (e: any) {
      if (e?.response?.status === 404) {
        setResult(null);
        setError('Ingen resultater funnet. Kjør jobben for å se nye underenheter.');
      } else {
        setError(e?.message || 'Ukjent feil ved henting av resultater.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadResult();
  }, []);

  const handleRunNow = async () => {
    setRunning(true);
    setRunMessage(null);
    try {
      await runBrregJobNow('brreg-underenhet-updates');
      setRunMessage('Jobben er satt i kø. Resultatene oppdateres om noen minutter.');
    } catch (e: any) {
      setRunMessage(`Feil ved kjøring: ${e?.message ?? 'Ukjent feil'}`);
    } finally {
      setRunning(false);
    }
  };

  const formatDate = (iso: string | null | undefined): string => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('nb-NO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  const renderAddress = (c: QaRunCandidate): string => {
    const parts: string[] = [];
    if (c.besokGateadresse) parts.push(c.besokGateadresse);
    if (c.besokPostnummer || c.besokPoststed)
      parts.push(`${c.besokPostnummer ?? ''} ${c.besokPoststed ?? ''}`.trim());
    return parts.join(', ') || '—';
  };

  return (
    <Container fluid className="mt-3">
      <PageHeader>Nye underenheter fra Brreg</PageHeader>

      <div className="d-flex align-items-center gap-2 mb-3">
        <Button variant="primary" size="sm" onClick={handleRunNow} disabled={running}>
          {running ? <Spinner animation="border" size="sm" className="me-1" /> : null}
          Kjør nå
        </Button>
        <Button variant="outline-secondary" size="sm" onClick={loadResult} disabled={loading}>
          {loading ? <Spinner animation="border" size="sm" className="me-1" /> : null}
          Oppdater
        </Button>
        {runMessage && <span className="text-muted small">{runMessage}</span>}
      </div>

      {loading && !result && (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      )}

      {error && !loading && <div className="alert alert-warning">{error}</div>}

      {result && (
        <>
          <div className="mb-3 d-flex flex-wrap gap-3 align-items-center">
            <span className="text-muted small">
              Siste kjøring: <strong>{formatDate(result.runAt)}</strong>
              {result.triggeredBy ? ` (${result.triggeredBy})` : ''}
            </span>
            <Badge bg="secondary">Hentet: {result.fetchedCount}</Badge>
            <Badge bg="info" text="dark">
              Ny: {result.nyCount}
            </Badge>
            <Badge bg="success">QA-kandidater: {result.qaCandidateCount}</Badge>
            {result.errorCount > 0 && <Badge bg="danger">Feil: {result.errorCount}</Badge>}
          </div>

          {result.qaCandidates.length === 0 ? (
            <p className="text-muted">Ingen QA-kandidater funnet i siste kjøring.</p>
          ) : (
            <Table striped bordered hover responsive size="sm">
              <thead className="table-dark">
                <tr>
                  <th>Bedriftsnr</th>
                  <th>Enhetsnavn</th>
                  <th>Morselskap (orgnr)</th>
                  <th>Morselskap</th>
                  <th>Bransje</th>
                  <th>Besøksadresse</th>
                  <th>E-post</th>
                  <th>Telefon</th>
                  <th>Hjemmeside</th>
                </tr>
              </thead>
              <tbody>
                {result.qaCandidates.map((c) => (
                  <tr key={c.bedriftsnr}>
                    <td>
                      <a
                        href={`https://www.brreg.no/bedrift/underenhet/${c.bedriftsnr}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {c.bedriftsnr}
                      </a>
                    </td>
                    <td>{c.enhetsnavn ?? '—'}</td>
                    <td>{c.parentOrgnr ?? '—'}</td>
                    <td>{c.parentNavn ?? '—'}</td>
                    <td>{c.bransjeForslag ?? '—'}</td>
                    <td>{renderAddress(c)}</td>
                    <td>{c.epost ? <a href={`mailto:${c.epost}`}>{c.epost}</a> : '—'}</td>
                    <td>{c.telefon ?? '—'}</td>
                    <td>
                      {c.hjemmeside ? (
                        <a href={c.hjemmeside} target="_blank" rel="noopener noreferrer">
                          {c.hjemmeside}
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}
    </Container>
  );
};

export default NyeUnderenheterPage;
